import { useState, useEffect } from 'react';
import { api } from './lib/api';

interface Client {
  id: string;
  nom: string;
  email?: string | null;
  siret?: string | null;
}

interface DocumentItem {
  id: string;
  type: 'devis' | 'facture';
  numero: string;
  date: string;
  statut: string;
  total_ht_centimes: number;
  total_tva_centimes: number;
  total_ttc_centimes: number;
  client?: { nom: string };
}

interface LigneForm {
  description: string;
  quantite: number;
  prix_unitaire_ht_centimes: number;
  taux_tva: number;
}

interface Props {
  entrepriseId: string;
  exerciceId: string;
}

type Tab = 'clients' | 'documents';

export default function Facturation({ entrepriseId, exerciceId }: Props) {
  const [tab, setTab] = useState<Tab>('documents');
  const [clients, setClients] = useState<Client[]>([]);
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showClientForm, setShowClientForm] = useState(false);
  const [clientNom, setClientNom] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientSiret, setClientSiret] = useState('');

  const [showDocForm, setShowDocForm] = useState(false);
  const [docType, setDocType] = useState<'devis' | 'facture'>('facture');
  const [docClientId, setDocClientId] = useState('');
  const [docDate, setDocDate] = useState(new Date().toISOString().slice(0, 10));
  const [docLignes, setDocLignes] = useState<LigneForm[]>([
    { description: '', quantite: 1, prix_unitaire_ht_centimes: 0, taux_tva: 20 },
  ]);
  const [docNotes, setDocNotes] = useState('');

  useEffect(() => { loadClients(); loadDocuments(); }, [entrepriseId]);

  const loadClients = async () => {
    try {
      const data = await api.clients.list(entrepriseId);
      setClients(data);
    } catch {}
  };

  const loadDocuments = async () => {
    try {
      const data = await api.documents.list(entrepriseId);
      setDocuments(data);
    } catch {}
  };

  const submitClient = async () => {
    setLoading(true);
    setError(null);
    try {
      await api.clients.create({ entreprise_id: entrepriseId, nom: clientNom, email: clientEmail || undefined, siret: clientSiret || undefined });
      setShowClientForm(false);
      setClientNom(''); setClientEmail(''); setClientSiret('');
      loadClients();
    } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  };

  const submitDocument = async () => {
    if (!docClientId) { setError('Sélectionnez un client'); return; }
    setLoading(true);
    setError(null);
    try {
      await api.documents.create({
        entreprise_id: entrepriseId,
        type: docType,
        client_id: docClientId,
        date: docDate,
        lignes: docLignes,
        notes: docNotes || undefined,
      });
      setShowDocForm(false);
      setDocLignes([{ description: '', quantite: 1, prix_unitaire_ht_centimes: 0, taux_tva: 20 }]);
      setDocNotes('');
      loadDocuments();
    } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  };

  const convertToFacture = async (docId: string) => {
    await api.documents.convertToFacture(docId, entrepriseId);
    loadDocuments();
  };

  const emitFacture = async (docId: string) => {
    await api.documents.emit(docId, entrepriseId, exerciceId);
    loadDocuments();
  };

  const updateLigne = (idx: number, field: keyof LigneForm, value: any) => {
    const newLignes = [...docLignes];
    (newLignes[idx] as any)[field] = value;
    setDocLignes(newLignes);
  };

  const addLigne = () => setDocLignes([...docLignes, { description: '', quantite: 1, prix_unitaire_ht_centimes: 0, taux_tva: 20 }]);
  const removeLigne = (idx: number) => setDocLignes(docLignes.filter((_, i) => i !== idx));

  const totalHT = docLignes.reduce((s, l) => s + l.quantite * l.prix_unitaire_ht_centimes, 0);
  const totalTVA = docLignes.reduce((s, l) => s + Math.round(l.quantite * l.prix_unitaire_ht_centimes * l.taux_tva / 100), 0);

  const statusLabel: Record<string, string> = { draft: 'Brouillon', sent: 'Envoyée', paid: 'Payée', overdue: 'En retard' };
  const statusColor: Record<string, string> = { draft: 'bg-gray-100 text-gray-600', sent: 'bg-blue-100 text-blue-700', paid: 'bg-green-100 text-green-700', overdue: 'bg-red-100 text-red-700' };

  const inputClass = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Facturation</h2>

      {error && <div className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-lg text-sm mb-4">{error}</div>}

      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit mb-6">
        <button
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${tab === 'clients' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
          onClick={() => setTab('clients')}
        >
          Clients
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${tab === 'documents' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
          onClick={() => setTab('documents')}
        >
          Documents
        </button>
      </div>

      {tab === 'clients' && (
        <div>
          <button onClick={() => setShowClientForm(!showClientForm)} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors mb-4">
            {showClientForm ? 'Annuler' : '+ Nouveau client'}
          </button>
          {showClientForm && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div><label className={labelClass}>Nom</label><input value={clientNom} onChange={e => setClientNom(e.target.value)} className={inputClass} /></div>
                <div><label className={labelClass}>Email</label><input value={clientEmail} onChange={e => setClientEmail(e.target.value)} className={inputClass} /></div>
                <div><label className={labelClass}>SIRET</label><input value={clientSiret} onChange={e => setClientSiret(e.target.value)} className={inputClass} /></div>
              </div>
              <button onClick={submitClient} disabled={loading || !clientNom} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Créer</button>
            </div>
          )}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Nom</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Email</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">SIRET</th>
                </tr>
              </thead>
              <tbody>
                {clients.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-gray-700 border-b border-gray-100">{c.nom}</td>
                    <td className="px-4 py-3 text-gray-500 border-b border-gray-100">{c.email || '-'}</td>
                    <td className="px-4 py-3 text-gray-500 border-b border-gray-100 font-mono text-xs">{c.siret || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'documents' && (
        <div>
          <button onClick={() => setShowDocForm(!showDocForm)} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors mb-4">
            {showDocForm ? 'Annuler' : '+ Nouveau document'}
          </button>
          {showDocForm && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className={labelClass}>Type</label>
                  <select value={docType} onChange={e => setDocType(e.target.value as any)} className={inputClass}>
                    <option value="devis">Devis</option>
                    <option value="facture">Facture</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Client</label>
                  <select value={docClientId} onChange={e => setDocClientId(e.target.value)} className={inputClass}>
                    <option value="">— Sélectionner —</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Date</label>
                  <input type="date" value={docDate} onChange={e => setDocDate(e.target.value)} className={inputClass} />
                </div>
              </div>

              <h4 className="text-sm font-semibold text-gray-700 mb-3">Lignes</h4>
              <div className="space-y-2 mb-3">
                {docLignes.map((l, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <input placeholder="Description" value={l.description} onChange={e => updateLigne(i, 'description', e.target.value)} className="flex-[2] px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    <input type="number" placeholder="Qté" value={l.quantite} onChange={e => updateLigne(i, 'quantite', parseInt(e.target.value) || 0)} className="w-16 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    <input type="number" placeholder="Prix HT (cts)" value={l.prix_unitaire_ht_centimes} onChange={e => updateLigne(i, 'prix_unitaire_ht_centimes', parseInt(e.target.value) || 0)} className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    <select value={l.taux_tva} onChange={e => updateLigne(i, 'taux_tva', parseFloat(e.target.value))} className="w-20 px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                      <option value={20}>20%</option><option value={10}>10%</option><option value={5.5}>5,5%</option><option value={2.1}>2,1%</option><option value={0}>0%</option>
                    </select>
                    {docLignes.length > 1 && <button onClick={() => removeLigne(i)} className="px-2 py-2 text-red-500 hover:text-red-700 text-sm">✕</button>}
                  </div>
                ))}
              </div>
              <button onClick={addLigne} className="text-blue-600 hover:text-blue-800 text-sm font-medium mb-4">+ Ligne</button>

              <div className="bg-gray-50 rounded-lg p-4 mb-4 text-sm">
                <p className="text-gray-700">Total HT : <strong>{(totalHT / 100).toFixed(2)} €</strong></p>
                <p className="text-gray-700">Total TVA : <strong>{(totalTVA / 100).toFixed(2)} €</strong></p>
                <p className="text-gray-900 font-bold mt-1">Total TTC : {((totalHT + totalTVA) / 100).toFixed(2)} €</p>
              </div>

              <div className="mb-4">
                <label className={labelClass}>Notes</label>
                <textarea value={docNotes} onChange={e => setDocNotes(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" rows={3} />
              </div>
              <button onClick={submitDocument} disabled={loading} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Créer</button>
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">N°</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Client</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Date</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">TTC</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Statut</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.map(d => (
                  <tr key={d.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-gray-700 border-b border-gray-100 font-mono text-xs">{d.numero}</td>
                    <td className="px-4 py-3 border-b border-gray-100">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${d.type === 'devis' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                        {d.type === 'devis' ? 'Devis' : 'Facture'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700 border-b border-gray-100">{d.client?.nom || '-'}</td>
                    <td className="px-4 py-3 text-gray-700 border-b border-gray-100">{d.date.slice(0, 10)}</td>
                    <td className="px-4 py-3 text-gray-700 border-b border-gray-100 text-right font-medium">{(d.total_ttc_centimes / 100).toFixed(2)} €</td>
                    <td className="px-4 py-3 border-b border-gray-100">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${statusColor[d.statut] || 'bg-gray-100 text-gray-600'}`}>
                        {statusLabel[d.statut] || d.statut}
                      </span>
                    </td>
                    <td className="px-4 py-3 border-b border-gray-100">
                      <div className="flex gap-2 items-center">
                        <a href={api.documents.getPdf(d.id, entrepriseId)} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800 text-xs font-medium">PDF</a>
                        {d.type === 'devis' && <button onClick={() => convertToFacture(d.id)} className="text-blue-600 hover:text-blue-800 text-xs font-medium">→ Facture</button>}
                        {d.type === 'facture' && d.statut === 'draft' && <button onClick={() => emitFacture(d.id)} className="text-green-600 hover:text-green-800 text-xs font-medium">Émettre</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
